import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import type { ManifestStation, ManifestShallow, ManifestSublocation, ManifestBridge, ActualsSnapshot, Licence } from './api'
import type { Actuals } from '@quicktide/shared'
import { holdAll } from './lib/plates'
import { getNotice, type Notice } from './api'
import { ATLASES } from './lib/atlases'
import { openToPhrase } from './lib/audience'
import { loadStations, loadShallows, loadSublocations, loadBridges, lastSyncTime, loadActuals, syncActuals, serverVersion as loadServerVersion, sync as runSync, loadLicence, storeLicence, syncLicence, type SyncProgress } from './sync'
import { APP_VERSION, APP_DATE } from './update'
import { clearCaches, startOfLocalDay } from './lib/tide'
import { dayPart } from '@quicktide/shared'
import { fix as devicePosition } from './lib/position'
import { translate, translateWith, browserLocale, type Locale, type MessageKey } from './i18n'

export type RefLevel = 'NAP' | 'LAT'
/** The three the old app had, and a fourth that picks between them by the sun */
export type Theme = 'day' | 'dusk' | 'night' | 'auto'
/** Day in light or in the dark blue: as the phone is set, or the reader's own choice */
export type DayLook = 'device' | 'light' | 'dark'
export type LocationView = 'map' | 'list' | 'nearby'
/** The four catalogs have their own id spaces, so a place is named by kind and id */
export type PlaceKind = 'station' | 'sublocation' | 'shallow' | 'bridge'
export interface Favourite { ref: string; kind: PlaceKind; id: string; name: string; detail: string }

/** Draught and the water the skipper wants left under the keel, both in centimetres */
const DEFAULT_DRAUGHT = 70
const DEFAULT_CLEARANCE = 0
const DEFAULT_MAST = 1200
const DEFAULT_MAST_CLEARANCE = 50

interface Settings {
  stationId: string | null
  /** Set when the chosen place is a shallow; stationId is then its reference station */
  shallowId: string | null
  /** Set when the chosen place is a derived location, or the shallow reaches its station through one */
  sublocationId: string | null
  /** Set when the chosen place is a bridge */
  bridgeId: string | null
  /** Air draught and the clearance the skipper wants above the mast, in centimetres */
  mastHeight: number
  mastClearance: number
  draught: number
  keelClearance: number
  refLevel: RefLevel
  locale: Locale
  theme: Theme
  /** Whether day follows the phone's light or dark setting, or is always the one chosen */
  dayLook: DayLook
  /** How the location chooser opens: the chart, or the alphabetical list */
  locationView: LocationView
  /** Places kept at hand, as "kind:id"; the old app's Favorieten tab, unbounded as there */
  favourites: string[]
  /** Set once the skipper has been through the boat's measurements, so the app asks only once */
  boatConfirmed: boolean
  /**
   * Set once the way in has been read: who QuickTide is for and what it shows without
   * registering. It is put back when a registration runs out, so the entry screen says so
   * once and then leaves the reader alone.
   */
  entrySeen: boolean
  /** The tidal stream atlas last looked at; null means the one around the chosen station */
  atlasId: string | null
  /** Set once the note about what the stream plates are has been read and put away */
  atlasNoteClosed: boolean
  /** How much time the tide chart shows, in hours; the old app kept its time scale too */
  chartSpanHours: number
  /**
   * The skipper's own correction to the predicted level, in centimetres: wind set-up,
   * or set-down in an easterly. The old app called it Verhoging/Verlaging and drew it as
   * a green line. It moves the astronomy and everything read off it, never a measurement.
   */
  surge: number
  /**
   * The reader's own soundings, by shallow: how deep they found it, in centimetres below
   * NAP as the catalogs count, and when. A published sounding ages and banks move; a
   * skipper who has just put a stick in the ground knows better than February's survey.
   */
  ownDepths: Record<string, { depth: number; at: string }>
}

const SETTINGS_KEY = 'quicktide.settings'

function defaults(): Settings {
  return {
    stationId: null, shallowId: null, sublocationId: null, bridgeId: null,
    mastHeight: DEFAULT_MAST, mastClearance: DEFAULT_MAST_CLEARANCE,
    draught: DEFAULT_DRAUGHT, keelClearance: DEFAULT_CLEARANCE,
    refLevel: 'NAP', locale: browserLocale(), theme: 'day', dayLook: 'device', locationView: 'map',
    favourites: [], boatConfirmed: false, entrySeen: false, atlasId: null, atlasNoteClosed: false, chartSpanHours: 24, surge: 0, ownDepths: {}
  }
}

function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY)
    if (raw) {
      const saved: Settings = { ...defaults(), ...JSON.parse(raw) }
      // someone who has already changed a measurement has answered the question once,
      // so the guided setup does not ambush a user who has been sailing with this app
      const d = defaults()
      if (saved.draught !== d.draught || saved.keelClearance !== d.keelClearance || saved.mastHeight !== d.mastHeight || saved.mastClearance !== d.mastClearance) saved.boatConfirmed = true
      return saved
    }
  } catch {
    // ignore corrupt settings
  }
  return defaults()
}

export const useAppStore = defineStore('app', () => {
  const settings = ref<Settings>(loadSettings())
  const stations = ref<ManifestStation[]>([])
  const shallows = ref<ManifestShallow[]>([])
  const sublocations = ref<ManifestSublocation[]>([])
  const bridges = ref<ManifestBridge[]>([])
  const selectedDay = ref<number>(startOfLocalDay(Date.now()))
  const sync = ref<SyncProgress>({ state: 'idle', done: 0, total: 0 })
  const lastSync = ref<string | null>(null)
  const online = ref(navigator.onLine)
  const dataVersion = ref(0)
  const actuals = ref<ActualsSnapshot | null>(null)
  const serverVersion = ref<string | null>(null)
  const updateAvailable = computed(() => !!serverVersion.value && serverVersion.value !== APP_VERSION && APP_VERSION !== 'dev')
  /** What this device may read; null until the server has been heard from for the first time */
  const licence = ref<Licence | null>(null)
  const registered = computed(() => licence.value?.state === 'granted')
  /** Who may activate just now, in words; null when everyone qualified may (see lib/audience.ts) */
  const openTo = computed(() => openToPhrase(licence.value?.openTo, settings.value.locale))
  /**
   * The days the reader may go to. An unregistered device holds the days up to yesterday and
   * nothing after, so this only stops the strip walking into empty screens -- the data is the
   * real limit, not this. It also makes such a device open on yesterday rather than on an
   * empty today.
   */
  const firstDay = computed(() => (licence.value?.from == null ? null : startOfLocalDay(licence.value.from)))
  const lastDay = computed(() => (licence.value?.to == null ? null : startOfLocalDay(licence.value.to - 1)))
  const dayAllowed = (day: number) => (firstDay.value === null || day >= firstDay.value) && (lastDay.value === null || day <= lastDay.value)
  /** The nearest day this device may read, for when the licence shrinks under the reader */
  const clampDay = (day: number) => Math.min(Math.max(day, firstDay.value ?? day), lastDay.value ?? day)

  watch(settings, s => localStorage.setItem(SETTINGS_KEY, JSON.stringify(s)), { deep: true })
  // before the theme: on automatic without the phone's position it reads the station, and its
  // watcher runs at once -- defined below it, that read hit the dead zone and left Safari white
  const station = computed(() => stations.value.find(s => s.id === settings.value.stationId) ?? null)
  /**
   * Which of the three is actually showing.
   *
   * On automatic it follows the sun where the reader is: white by day, warm through civil
   * twilight, red once the horizon has gone and night vision is worth keeping. A light
   * sensor would be the obvious thing, and the browsers have no usable one — but a hand
   * over the phone is not nightfall and a chart-table lamp is not dawn, so the sun is the
   * better answer anyway.
   *
   * Where the phone is beats where the tide station is: a skipper reading Harlingen from
   * the Elbe, or from a berth in Scotland over the winter, wants the dark that is outside
   * their own window. The device's position is only ever used if it has already been given
   * — this never asks — and the station stands in when it has not.
   */
  const minute = ref(Date.now())
  window.setInterval(() => { minute.value = Date.now() }, 60_000)
  const showingTheme = computed<Exclude<Theme, 'auto'>>(() => {
    if (settings.value.theme !== 'auto') return settings.value.theme
    const phone = devicePosition.value
    if (phone) return dayPart(minute.value, phone.lat, phone.lon)
    const here = station.value
    if (here?.lat === undefined || here.lon === undefined) return 'day'
    return dayPart(minute.value, here.lat, here.lon)
  })
  // the browser's own chrome follows the page: pale by day, and not a white bar over the night
  const chromeFollowsPage = () => {
    const bg = getComputedStyle(document.documentElement).getPropertyValue('--bg').trim()
    if (bg) document.querySelector('meta[name="theme-color"]')?.setAttribute('content', bg)
  }
  watch(showingTheme, t => {
    document.documentElement.dataset['theme'] = t
    chromeFollowsPage()
  }, { immediate: true })
  // day comes in light or in the old blue: as the phone is set, unless the reader chose one
  const phoneDark = window.matchMedia('(prefers-color-scheme: dark)')
  const deviceDark = ref(phoneDark.matches)
  phoneDark.addEventListener('change', e => { deviceDark.value = e.matches })
  const dayDark = computed(() => settings.value.dayLook === 'device' ? deviceDark.value : settings.value.dayLook === 'dark')
  watch(dayDark, dark => {
    document.documentElement.dataset['day'] = dark ? 'dark' : 'light'
    chromeFollowsPage()
  }, { immediate: true })
  watch(() => settings.value.locale, l => document.documentElement.lang = l, { immediate: true })
  // back in range: the measured levels are the thing most likely to have moved on
  window.addEventListener('online', () => { online.value = true; void refreshActuals() })
  window.addEventListener('offline', () => { online.value = false })

  /** The chosen shallow, if the chosen place is one; its tide comes from `station` */
  const shallow = computed(() => shallows.value.find(s => s.id === settings.value.shallowId) ?? null)
  /**
   * The derived place in play: the chosen one, or the one a chosen shallow reaches its
   * station through. Its offsets come between the station and the place on screen.
   */
  const bridge = computed(() => bridges.value.find(b => b.id === settings.value.bridgeId) ?? null)
  const sublocation = computed(() => {
    const id = shallow.value?.via ?? bridge.value?.via ?? settings.value.sublocationId
    return sublocations.value.find(s => s.id === id) ?? null
  })
  /** What the screens are showing, in order of precedence */
  const place = computed(() => shallow.value ?? bridge.value ?? (settings.value.sublocationId ? sublocation.value : null) ?? station.value)
  /**
   * A warning that belongs to the place on screen -- only shallows carry one, from the
   * catalog. The old app put it in front of the reader: a box when the place was chosen and
   * an icon by its name to bring it back. So does this. The box comes once per choice, and
   * not again for the same text, as the old app did (obj_LocatieInfo.pas).
   */
  const placeWarning = computed(() => shallow.value?.warning?.trim() || null)
  const warningOpen = ref(false)
  let lastWarned: string | null = null
  watch(placeWarning, w => {
    if (w && w !== lastWarned) warningOpen.value = true
    lastWarned = w ?? lastWarned
  })

  /** The reader's own sounding for the chosen shallow, if they took one */
  const ownDepth = computed(() => {
    const id = shallow.value?.id
    return id ? settings.value.ownDepths[id] ?? null : null
  })
  /**
   * The depth every screen should work from: the reader's own where they have taken one,
   * the published survey otherwise.
   */
  const soundedDepth = computed(() => ownDepth.value?.depth ?? shallow.value?.depth ?? null)

  function setOwnDepth(depthCm: number) {
    const id = shallow.value?.id
    if (!id) return
    settings.value.ownDepths = { ...settings.value.ownDepths, [id]: { depth: Math.round(depthCm), at: new Date().toISOString() } }
  }
  function clearOwnDepth() {
    const id = shallow.value?.id
    if (!id) return
    const { [id]: _gone, ...rest } = settings.value.ownDepths
    settings.value.ownDepths = rest
  }

  /** Measured and forecast levels of the selected station, if the server had any */
  const stationActuals = computed<Actuals | null>(() => (station.value && actuals.value?.stations[station.value.id]) ?? null)
  const t = (key: MessageKey) => translate(settings.value.locale, key)
  /** For messages that wrap a value, where the word order differs per language */
  const tv = (key: MessageKey, value: string) => translateWith(settings.value.locale, key, value)

  /** Convert a level in cm NAP/NHN to the chosen reference level, in metres */
  function displayLevel(cmNap: number): number {
    const offset = settings.value.refLevel === 'LAT' && station.value?.altRef !== undefined ? station.value.altRef : 0
    return (cmNap - offset) / 100
  }
  /**
   * What to call each datum on screen, as the country that publishes it calls it: ALAT on
   * the Dutch coast, OLW on the rivers, SKN in Germany, against NAP or NHN. The name follows
   * the station, never the reader's language: it says what the numbers are measured from,
   * and a Dutch level is against NAP whichever language it is read in.
   */
  const altDatumName = computed(() =>
    station.value?.altRefName ?? (station.value?.area === 'DE' ? 'SKN' : 'ALAT'))
  const napDatumName = computed(() => (station.value?.area === 'DE' ? 'NHN' : 'NAP'))
  const refLevelName = computed(() =>
    settings.value.refLevel === 'LAT' ? altDatumName.value : napDatumName.value)

  /**
   * The administrator's message: asked for on every start and every return to the app, and
   * shown until clicked away. Which ones were clicked away is remembered by id, so the same
   * message does not come back, and a changed one does.
   */
  const notice = ref<Notice | null>(null)
  const NOTICES_KEY = 'quicktide.notices'
  const dismissedNotices = (): string[] => {
    try {
      return JSON.parse(localStorage.getItem(NOTICES_KEY) ?? '[]') as string[]
    } catch {
      return []
    }
  }
  async function refreshNotice() {
    const n = await getNotice()
    notice.value = n && !dismissedNotices().includes(n.id) ? n : null
  }
  function dismissNotice() {
    if (!notice.value) return
    // the last few are enough: an old id never comes back
    const ids = [...dismissedNotices().filter(id => id !== notice.value!.id), notice.value.id].slice(-20)
    try {
      localStorage.setItem(NOTICES_KEY, JSON.stringify(ids))
    } catch {
      // not remembered: it shows once more next time, which is the lesser harm
    }
    notice.value = null
  }

  async function init() {
    void refreshNotice()
    licence.value = await loadLicence()
    stations.value = await loadStations()
    shallows.value = await loadShallows()
    sublocations.value = await loadSublocations()
    bridges.value = await loadBridges()
    lastSync.value = await lastSyncTime()
    actuals.value = await loadActuals()
    serverVersion.value = await loadServerVersion()
    chooseDefaultPlace()
    await synchronise()
  }

  /**
   * Somewhere to show when nothing has been chosen yet, so nobody opens on an empty screen.
   * A trial opens on the Vingegat, a shallow off Harlingen: it shows at once what the app
   * does that a tide table does not. A registered device opens on Harlingen. Run at start and
   * after every sync, because on a new device the catalogs only arrive with the first one; a
   * place the reader chose is never replaced.
   */
  function chooseDefaultPlace() {
    const s = settings.value
    if (s.stationId || s.shallowId || s.sublocationId || s.bridgeId) return
    if (!registered.value && shallows.value.some(x => x.id === 'VINGEGAT')) {
      selectShallow('VINGEGAT')
      return
    }
    if (stations.value.some(x => x.id === 'harlingen.waddenzee')) s.stationId = 'harlingen.waddenzee'
  }

  /** Whether new measured or forecast levels came in, which the files count does not show */
  async function synchronise(): Promise<{ levels: boolean }> {
    if (sync.value.state === 'running') return { levels: false }
    await runSync(p => { sync.value = p })
    stations.value = await loadStations()
    shallows.value = await loadShallows()
    sublocations.value = await loadSublocations()
    bridges.value = await loadBridges()
    lastSync.value = await lastSyncTime()
    serverVersion.value = await loadServerVersion()
    licence.value = await loadLicence()
    selectedDay.value = clampDay(selectedDay.value)
    chooseDefaultPlace()
    clearCaches()
    dataVersion.value++
    // a registered device keeps every stream atlas on board, fetched in the background
    if (registered.value) void holdAll(ATLASES.map(a => a.id))
    return { levels: await refreshActuals() }
  }

  /**
   * Ask the server what this device may read. Called when the app comes back to the
   * foreground, which is how a device learns that the link in the mail has been clicked
   * -- often on another device altogether.
   */
  async function refreshLicence() {
    void refreshNotice()
    const fresh = await syncLicence()
    if (fresh) {
      const was = licence.value?.state
      if (fresh.state === 'expired' && was !== 'expired') settings.value.entrySeen = false
      licence.value = fresh
      selectedDay.value = clampDay(selectedDay.value)
      // newly registered: the whole range is waiting to be downloaded
      if (was && was !== fresh.state) await synchronise()
    }
    return licence.value
  }

  /** After a registration step, where the server already handed back the new licence */
  async function setLicence(fresh: Licence | null) {
    if (!fresh) return
    const was = licence.value?.state
    licence.value = await storeLicence(fresh)
    selectedDay.value = clampDay(selectedDay.value)
    if (was && was !== fresh.state) await synchronise()
  }

  /** Cheap enough to run every time the app comes to the foreground; true when something new came */
  async function refreshActuals(): Promise<boolean> {
    const snap = await syncActuals()
    const fresh = !!snap && snap.generatedAt !== actuals.value?.generatedAt
    if (snap) actuals.value = snap
    return fresh
  }

  function selectStation(id: string) {
    settings.value.stationId = id
    settings.value.shallowId = null
    settings.value.sublocationId = null
    settings.value.bridgeId = null
  }

  /** A derived place: the station carries the tide, the offsets reshape it */
  function selectSublocation(id: string) {
    const s = sublocations.value.find(x => x.id === id)
    if (!s) return
    settings.value.sublocationId = id
    settings.value.shallowId = null
    settings.value.bridgeId = null
    settings.value.stationId = s.station
  }

  /** Choosing a shallow also chooses the station its tide is derived from */
  function selectShallow(id: string) {
    const s = shallows.value.find(x => x.id === id)
    if (!s) return
    settings.value.shallowId = id
    settings.value.sublocationId = null
    settings.value.bridgeId = null
    settings.value.stationId = s.station
  }

  /** A bridge: the tide has to stay below its clearance, not rise above a depth */
  function selectBridge(id: string) {
    const b = bridges.value.find(x => x.id === id)
    if (!b) return
    settings.value.bridgeId = id
    settings.value.shallowId = null
    settings.value.sublocationId = null
    settings.value.stationId = b.station
  }

  /** What is on screen, in the same order of precedence as `place` */
  const currentRef = computed<string | null>(() => {
    const s = settings.value
    if (s.bridgeId) return `bridge:${s.bridgeId}`
    if (s.shallowId) return `shallow:${s.shallowId}`
    if (s.sublocationId) return `sublocation:${s.sublocationId}`
    return s.stationId ? `station:${s.stationId}` : null
  })

  const metres = (cm: number) => `${(cm / 100).toFixed(2)} m`

  /** A favourite that is no longer in the catalogs quietly drops out of the list */
  function resolve(ref: string): Favourite | null {
    const cut = ref.indexOf(':')
    if (cut < 0) return null
    const kind = ref.slice(0, cut) as PlaceKind
    const id = ref.slice(cut + 1)
    if (kind === 'station') {
      const x = stations.value.find(s => s.id === id)
      return x ? { ref, kind, id, name: x.name, detail: x.id } : null
    }
    if (kind === 'sublocation') {
      const x = sublocations.value.find(s => s.id === id)
      return x ? { ref, kind, id, name: x.name, detail: x.area ?? '' } : null
    }
    if (kind === 'shallow') {
      const x = shallows.value.find(s => s.id === id)
      return x ? { ref, kind, id, name: x.name, detail: metres(x.depth) } : null
    }
    const x = bridges.value.find(b => b.id === id)
    return x ? { ref, kind, id, name: x.name, detail: metres(x.height) } : null
  }

  const favourites = computed<Favourite[]>(() =>
    settings.value.favourites.map(resolve).filter((f): f is Favourite => f !== null))
  const isFavourite = computed(() => !!currentRef.value && settings.value.favourites.includes(currentRef.value))
  const favourited = (ref: string) => settings.value.favourites.includes(ref)

  /** The star: on for the place on screen unless another is named */
  function toggleFavourite(ref: string | null = currentRef.value) {
    if (!ref) return
    const list = settings.value.favourites
    const at = list.indexOf(ref)
    if (at < 0) list.push(ref)
    else list.splice(at, 1)
  }

  /** Choose a place by the same "kind:id" a favourite is stored as */
  function selectPlace(ref: string) {
    const cut = ref.indexOf(':')
    if (cut < 0) return
    const id = ref.slice(cut + 1)
    const kind = ref.slice(0, cut) as PlaceKind
    if (kind === 'station') selectStation(id)
    else if (kind === 'sublocation') selectSublocation(id)
    else if (kind === 'shallow') selectShallow(id)
    else selectBridge(id)
  }

  return { settings, stations, shallows, sublocations, bridges, station, shallow, sublocation, bridge, place, stationActuals, actuals, serverVersion, updateAvailable, appVersion: APP_VERSION, appDate: APP_DATE, selectedDay, sync, lastSync, online, dataVersion, t, tv, displayLevel, refLevelName, altDatumName, napDatumName, showingTheme, init, synchronise, refreshActuals,
    notice, refreshNotice, dismissNotice, placeWarning, warningOpen,
    licence, registered, openTo, firstDay, lastDay, dayAllowed, clampDay, refreshLicence, setLicence, selectStation, selectShallow, selectSublocation, selectBridge, currentRef, favourites, isFavourite, favourited, toggleFavourite, selectPlace,
    ownDepth, soundedDepth, setOwnDepth, clearOwnDepth }
})
